/**
 * Simple Firebase-backed login UI for the admin portal.
 *
 * @remarks
 * This component intentionally uses the lightweight firebase wrapper (`src/utils/firebase.ts` → `src/lib/firebase.ts`)
 * to avoid circular dependencies during auth bootstrap.
 *
 * Logging note: keep logs low-noise and avoid PII. Prefer `warn`/`error` for durable signal.
 */

import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  type User,
} from "firebase/auth";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { resolvePopupLoginGuidance } from "./loginErrorMessages";
import { getErrorMessage as getPublicErrorMessage } from "../../utils/errorHandling";
import { auth, getFirebaseAuthDebugSnapshot } from "../../utils/firebase";

import {
  clearAdminCrossTabSignOutNotice,
  hasAdminCrossTabSignOutNotice,
} from "@/auth/adminCrossTabSignOut";
import { useAuthState } from "@/auth/helloCashflowDomain/useAuthState";
import { adminUiCopy } from "@/copy/adminUiCopy";
import { getAdminBuildInfo } from "@/utils/buildInfo";
import { normalizeAdminFrontendPath } from "@/utils/navigation/normalizeAdminFrontendPath";
import {
  VNextButton,
  VNextCard,
  VNextField,
  VNextInput,
  VNextNotice,
  VNextPill,
} from "@/vnext/primitives";
import "@/vnext/theme/vnext.css";
import "./simpleLogin.css";

const logger = FrontendLoggerFactory.getLogger().withComponent("SimpleLogin");

const sanitizeReturnUrl = (value: string | null): string | null => {
  if (!value) {
    return null;
  }

  // Security: only allow relative paths to avoid open redirects.
  if (!value.startsWith("/") || value.startsWith("//")) {
    return null;
  }

  return normalizeAdminFrontendPath(value);
};

type LoginAuthReason = "auth_session_lost";

const resolveQueryReturnUrl = (search: string): string | null => {
  const params = new URLSearchParams(search);
  return sanitizeReturnUrl(params.get("return_url") ?? params.get("returnUrl"));
};

const resolveQueryAuthReason = (search: string): LoginAuthReason | null => {
  const params = new URLSearchParams(search);
  const authReason = params.get("auth_reason");

  return authReason === "auth_session_lost" ? authReason : null;
};

const getAuthReasonMessage = (
  authReason: LoginAuthReason | null,
): string | null => {
  if (authReason === "auth_session_lost") {
    return adminUiCopy.authLogin.authSessionLost;
  }

  return null;
};

const resolveSafeLoginDestination = (value: unknown): string => {
  if (typeof value === "string") {
    return sanitizeReturnUrl(value) ?? "/";
  }

  if (!value || typeof value !== "object") {
    return "/";
  }

  const pathname =
    typeof (value as { pathname?: unknown }).pathname === "string"
      ? (value as { pathname: string }).pathname
      : null;
  if (!pathname) {
    return "/";
  }

  const search =
    typeof (value as { search?: unknown }).search === "string"
      ? (value as { search: string }).search
      : "";
  const hash =
    typeof (value as { hash?: unknown }).hash === "string"
      ? (value as { hash: string }).hash
      : "";

  return sanitizeReturnUrl(`${pathname}${search}${hash}`) ?? "/";
};

interface FinalizeLoginArgs {
  redirectTarget: string;
  loginMode: "email" | "google" | "quickbooks" | "token" | "xero";
  providerId: string | null;
  user: User;
  successOperation: string;
  successMessage: string;
  missingAdminOperation: string;
  missingAdminMessage: string;
  flowStartedAtMs?: number;
}

interface PopupLoginArgs {
  loginMode: "google" | "quickbooks" | "xero";
  provider: GoogleAuthProvider | OAuthProvider;
  startOperation: string;
  successOperation: string;
  successMessage: string;
  missingAdminOperation: string;
  missingAdminMessage: string;
  failureOperation: string;
  failureMessage: string;
  failureFallbackMessage: string;
}

interface ProviderDiagnostics {
  hasCustomParameters: boolean;
  providerId: string | null;
  scopes: string[];
}

interface HelperProbeResult {
  contentType: string | null;
  ok: boolean;
  path: string;
  redirected: boolean;
  signature:
    | "firebase_handler"
    | "firebase_init_json"
    | "site_not_found"
    | "spa_html"
    | "unknown";
  status: number | null;
}

const isTestRuntime = () =>
  import.meta.env.MODE === "test" || import.meta.env.NODE_ENV === "test";

const resolveBrowserFamily = () => {
  if (typeof navigator === "undefined") {
    return {
      browserFamily: "unknown",
      isFirefox: false,
      isSafari: false,
    };
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const isFirefox = userAgent.includes("firefox");
  const isSafari =
    userAgent.includes("safari") && !userAgent.includes("chrome");

  return {
    browserFamily: isFirefox ? "firefox" : isSafari ? "safari" : "other",
    isFirefox,
    isSafari,
  };
};

const getProviderDiagnostics = (
  provider: GoogleAuthProvider | OAuthProvider,
): ProviderDiagnostics => {
  const providerCandidate = provider as {
    providerId?: unknown;
    scopes?: unknown;
    getScopes?: () => unknown;
    getCustomParameters?: () => unknown;
  };
  const resolvedScopes = providerCandidate.getScopes?.();
  const scopesValue = Array.isArray(providerCandidate.scopes)
    ? providerCandidate.scopes
    : Array.isArray(resolvedScopes)
      ? resolvedScopes
      : [];
  const customParameters = providerCandidate.getCustomParameters?.();

  return {
    providerId:
      typeof providerCandidate.providerId === "string"
        ? providerCandidate.providerId
        : null,
    scopes: scopesValue.filter(
      (scope): scope is string => typeof scope === "string",
    ),
    hasCustomParameters:
      typeof customParameters === "object" &&
      customParameters !== null &&
      Object.keys(customParameters).length > 0,
  };
};

const classifyErrorMessage = (message: string | null) => {
  if (!message) {
    return "missing";
  }

  if (message.includes("popup-closed-by-user")) {
    return "popup_closed";
  }

  if (message.includes("popup-blocked")) {
    return "popup_blocked";
  }

  if (message.includes("network-request-failed")) {
    return "network";
  }

  if (message.includes("web-storage-unsupported")) {
    return "web_storage";
  }

  if (message.includes("unauthorized-domain")) {
    return "unauthorized_domain";
  }

  return "other";
};

const classifyHelperBody = (body: string): HelperProbeResult["signature"] => {
  if (body.includes("fireauth.oauthhelper.widget.initialize")) {
    return "firebase_handler";
  }

  if (body.includes('"projectId"') && body.includes('"apiKey"')) {
    return "firebase_init_json";
  }

  if (body.includes("Site Not Found")) {
    return "site_not_found";
  }

  if (
    body.includes("injectIntoGlobalHook") ||
    body.includes('id="root"') ||
    body.includes('meta name="hc-shell-track"')
  ) {
    return "spa_html";
  }

  return "unknown";
};

const probeCurrentOriginFirebaseHelpers = async (): Promise<{
  handler: HelperProbeResult;
  init: HelperProbeResult;
} | null> => {
  if (typeof window === "undefined" || isTestRuntime()) {
    return null;
  }

  const probePath = async (path: string): Promise<HelperProbeResult> => {
    const response = await fetch(path, {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
    });
    const body = await response.text();

    return {
      contentType: response.headers.get("content-type"),
      ok: response.ok,
      path,
      redirected: response.redirected,
      signature: classifyHelperBody(body.slice(0, 512)),
      status: response.status,
    };
  };

  return {
    handler: await probePath("/__/auth/handler"),
    init: await probePath("/__/firebase/init.json"),
  };
};

const attachPopupLifecycleLogging = ({
  loginMode,
  providerId,
  startedAtMs,
}: {
  loginMode: PopupLoginArgs["loginMode"];
  providerId: string | null;
  startedAtMs: number;
}) => {
  if (typeof window === "undefined" || isTestRuntime()) {
    return () => {};
  }

  const logLifecycle = (
    operation:
      | "login_popup_window_blur"
      | "login_popup_window_focus"
      | "login_popup_visibility_changed",
    message: string,
  ) => {
    logger.debug(message, {
      operation,
      loginMode,
      providerId,
      elapsedMs: Date.now() - startedAtMs,
      documentVisibilityState: document.visibilityState,
    });
  };

  const handleBlur = () => {
    logLifecycle(
      "login_popup_window_blur",
      "Login window lost focus during popup flow",
    );
  };
  const handleFocus = () => {
    logLifecycle(
      "login_popup_window_focus",
      "Login window regained focus during popup flow",
    );
  };
  const handleVisibilityChange = () => {
    logLifecycle(
      "login_popup_visibility_changed",
      "Login page visibility changed during popup flow",
    );
  };

  window.addEventListener("blur", handleBlur);
  window.addEventListener("focus", handleFocus);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    window.removeEventListener("blur", handleBlur);
    window.removeEventListener("focus", handleFocus);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
};

const SimpleLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authenticatedUser } = useAuthState();
  const from = location.state?.from ?? "/";
  const safeFrom = resolveSafeLoginDestination(from);
  const queryReturnUrl = resolveQueryReturnUrl(location.search);
  const queryAuthReason = resolveQueryAuthReason(location.search);
  const redirectTarget = queryReturnUrl ?? safeFrom;

  const tokenLoginHandledRef = useRef(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  // Read once on entry; cleared after mount so it shows only for this visit.
  const [signedOutInAnotherTab] = useState(hasAdminCrossTabSignOutNotice);
  useEffect(() => {
    clearAdminCrossTabSignOutNotice();
  }, []);
  const [loading, setLoading] = useState(false);
  const { buildLabel } = getAdminBuildInfo();

  useEffect(() => {
    logger.debug("Login route entered", {
      operation: "login_route_enter",
      pathname: location.pathname,
      queryReturnUrlPresent: Boolean(queryReturnUrl),
      redirectTargetPresent: Boolean(redirectTarget),
      hasRouterStateFrom: Boolean(location.state?.from),
      hasTokenLoginParam: new URLSearchParams(location.search).has("token"),
      loading,
    });
  }, [
    loading,
    queryAuthReason,
    location.pathname,
    location.search,
    location.state,
    queryReturnUrl,
    redirectTarget,
  ]);

  useEffect(() => {
    const authReasonMessage = getAuthReasonMessage(queryAuthReason);
    if (!authReasonMessage) {
      return;
    }

    logger.notice("Login route received an auth recovery notice", {
      operation: "login_auth_recovery_notice",
      pathname: location.pathname,
      authReason: queryAuthReason,
      queryReturnUrlPresent: Boolean(queryReturnUrl),
      redirectTargetPresent: Boolean(redirectTarget),
    });
    setError((currentError) => currentError || authReasonMessage);
  }, [location.pathname, queryAuthReason, queryReturnUrl, redirectTarget]);

  const finalizeLogin = async ({
    redirectTarget,
    loginMode,
    providerId,
    user,
    successOperation,
    successMessage,
    missingAdminOperation,
    missingAdminMessage,
    flowStartedAtMs,
  }: FinalizeLoginArgs): Promise<void> => {
    const claims = await user.getIdTokenResult();
    const hasAdminClaim = !!claims.claims["admin"];
    const authSnapshot = getFirebaseAuthDebugSnapshot();

    logger.debug("Login claims check completed", {
      operation: "login_finalize_claims_checked",
      loginMode,
      providerId,
      userIdPresent: Boolean(user.uid),
      hasAdminClaim,
      redirectTargetPresent: Boolean(redirectTarget),
      claimsSource: "user.getIdTokenResult",
      elapsedMs:
        typeof flowStartedAtMs === "number"
          ? Date.now() - flowStartedAtMs
          : undefined,
    });

    if (hasAdminClaim) {
      logger.debug(successMessage, {
        operation: successOperation,
        userIdPresent: Boolean(user.uid),
        redirectTargetPresent: Boolean(redirectTarget),
        loginMode,
        providerId,
      });
    } else {
      logger.warn(missingAdminMessage, {
        operation: missingAdminOperation,
        userIdPresent: Boolean(user.uid),
        redirectTargetPresent: Boolean(redirectTarget),
        loginMode,
        providerId,
      });
    }
    logger.info(
      "Login accepted; waiting for shared auth observer before leaving the login route",
      {
        operation: "login_waiting_for_auth_observer",
        userIdPresent: Boolean(user.uid),
        redirectTargetPresent: Boolean(redirectTarget),
        loginMode,
        providerId,
        hasAdminClaim,
        authObserverWaiting: true,
        currentAuthUserPresent: authSnapshot.currentUserPresent,
        isAuthLoading: authSnapshot.isAuthLoading,
        routePath: location.pathname,
        elapsedMsSinceClick:
          typeof flowStartedAtMs === "number"
            ? Date.now() - flowStartedAtMs
            : undefined,
      },
    );
    logger.debug("Captured auth observer snapshot after successful login", {
      operation: "login_waiting_for_auth_observer_snapshot",
      loginMode,
      providerId,
      userIdPresent: Boolean(user.uid),
      redirectTargetPresent: Boolean(redirectTarget),
      loading,
      currentAuthUserPresent: authSnapshot.currentUserPresent,
      isAuthLoading: authSnapshot.isAuthLoading,
      routePath: location.pathname,
      elapsedMsSinceClick:
        typeof flowStartedAtMs === "number"
          ? Date.now() - flowStartedAtMs
          : undefined,
    });
  };

  const handlePopupLogin = async ({
    loginMode,
    provider,
    startOperation,
    successOperation,
    successMessage,
    missingAdminOperation,
    missingAdminMessage,
    failureOperation,
    failureMessage,
    failureFallbackMessage,
  }: PopupLoginArgs): Promise<void> => {
    const startedAtMs = Date.now();
    const providerDiagnostics = getProviderDiagnostics(provider);
    const authSnapshot = getFirebaseAuthDebugSnapshot();
    const { browserFamily, isFirefox, isSafari } = resolveBrowserFamily();
    const isAuthDomainSameOrigin =
      authSnapshot.currentHost === authSnapshot.authDomain;
    let detachPopupLifecycleLogging = () => {};

    setLoading(true);
    setError("");

    try {
      logger.debug("Prepared popup login precheck", {
        operation: "login_popup_precheck",
        loginMode,
        providerId: providerDiagnostics.providerId,
        redirectTargetPresent: Boolean(redirectTarget),
        pathname: location.pathname,
        queryReturnUrlPresent: Boolean(queryReturnUrl),
        hasRouterStateFrom: Boolean(location.state?.from),
        loading,
        authCurrentUserPresent: authSnapshot.currentUserPresent,
        isFirebaseReady: !authSnapshot.isAuthLoading,
        isAuthLoading: authSnapshot.isAuthLoading,
        authDomain: authSnapshot.authDomain,
        origin: authSnapshot.origin,
        currentHost: authSnapshot.currentHost,
        browserFamily,
        isFirefox,
        isSafari,
      });
      logger.debug("Popup provider configured", {
        operation: "login_popup_provider_configured",
        loginMode,
        providerId: providerDiagnostics.providerId,
        scopes: providerDiagnostics.scopes,
        hasCustomParameters: providerDiagnostics.hasCustomParameters,
        authDomain: authSnapshot.authDomain,
        authAppName: authSnapshot.appName,
        origin: authSnapshot.origin,
      });

      try {
        const helperProbe = await probeCurrentOriginFirebaseHelpers();
        if (helperProbe) {
          logger.debug("Current-origin Firebase helper probe completed", {
            operation: "login_popup_same_origin_helper_probe_complete",
            loginMode,
            providerId: providerDiagnostics.providerId,
            handlerStatus: helperProbe.handler.status,
            handlerOk: helperProbe.handler.ok,
            handlerSignature: helperProbe.handler.signature,
            handlerContentType: helperProbe.handler.contentType,
            initStatus: helperProbe.init.status,
            initOk: helperProbe.init.ok,
            initSignature: helperProbe.init.signature,
            initContentType: helperProbe.init.contentType,
          });
        }
      } catch (helperProbeError) {
        logger.warn("Current-origin Firebase helper probe failed", {
          operation: "login_popup_same_origin_helper_probe_failed",
          loginMode,
          providerId: providerDiagnostics.providerId,
          errorName:
            helperProbeError instanceof Error
              ? helperProbeError.name
              : "unknown",
          errorMessageClass: classifyErrorMessage(
            helperProbeError instanceof Error ? helperProbeError.message : null,
          ),
        });
      }

      if (!isAuthDomainSameOrigin) {
        logger.notice(
          "Popup login is using a cross-origin Firebase auth helper",
          {
            operation: "login_popup_cross_origin_helper_detected",
            loginMode,
            providerId: providerDiagnostics.providerId,
            authDomain: authSnapshot.authDomain,
            currentHost: authSnapshot.currentHost,
            origin: authSnapshot.origin,
            isFirefox,
            isSafari,
          },
        );
      }
      logger.debug(`Starting ${startOperation} login`, {
        operation: startOperation,
        loginMode,
        providerId: providerDiagnostics.providerId,
        pathname: location.pathname,
        queryReturnUrlPresent: Boolean(queryReturnUrl),
        redirectTargetPresent: Boolean(redirectTarget),
        hasRouterStateFrom: Boolean(location.state?.from),
      });
      logger.info("Popup login requested", {
        operation: "login_popup_open_requested",
        loginMode,
        providerId: providerDiagnostics.providerId,
        redirectTargetPresent: Boolean(redirectTarget),
        origin: authSnapshot.origin,
        documentVisibilityState:
          typeof document === "undefined" ? null : document.visibilityState,
      });
      detachPopupLifecycleLogging = attachPopupLifecycleLogging({
        loginMode,
        providerId: providerDiagnostics.providerId,
        startedAtMs,
      });
      const result = await signInWithPopup(auth, provider);
      logger.debug("Popup login resolved with a Firebase credential", {
        operation: "login_popup_open_resolved",
        loginMode,
        providerId: providerDiagnostics.providerId,
        userIdPresent: Boolean(result.user.uid),
        hasAdminClaim: null,
        redirectTargetPresent: Boolean(redirectTarget),
        claimsLoaded: false,
        elapsedMs: Date.now() - startedAtMs,
      });
      await finalizeLogin({
        redirectTarget,
        loginMode,
        providerId: providerDiagnostics.providerId,
        user: result.user,
        successOperation,
        successMessage,
        missingAdminOperation,
        missingAdminMessage,
        flowStartedAtMs: startedAtMs,
      });
    } catch (loginError) {
      const errorCode =
        typeof loginError === "object" &&
        loginError !== null &&
        "code" in loginError &&
        typeof (loginError as { code?: unknown }).code === "string"
          ? (loginError as { code: string }).code
          : null;
      const errorName =
        loginError instanceof Error ? loginError.name : "unknown";
      const errorMessage =
        loginError instanceof Error ? loginError.message : null;

      logger.warn("Popup login failure was classified", {
        operation: "login_popup_failure_classified",
        loginMode,
        providerId: providerDiagnostics.providerId,
        errorCode,
        errorName,
        errorMessageClass: classifyErrorMessage(errorMessage),
        elapsedMs: Date.now() - startedAtMs,
        documentVisibilityState:
          typeof document === "undefined" ? null : document.visibilityState,
        popupLikelyClosed: errorCode === "auth/popup-closed-by-user",
        redirectTargetPresent: Boolean(redirectTarget),
        authCurrentUserPresentAfterFailure: Boolean(auth.currentUser),
        isAuthLoading: getFirebaseAuthDebugSnapshot().isAuthLoading,
      });
      logger.error(
        failureMessage,
        loginError instanceof Error ? loginError : undefined,
        {
          operation: failureOperation,
          errorCode,
          errorName,
          errorMessageClass: classifyErrorMessage(errorMessage),
          elapsedMs: Date.now() - startedAtMs,
        },
      );
      setError(
        resolvePopupLoginGuidance(loginError) ??
          getPublicErrorMessage(loginError, failureFallbackMessage),
      );
    } finally {
      detachPopupLifecycleLogging();
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authenticatedUser || loading) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.has("token")) {
      return;
    }

    // Successful sign-ins intentionally rely on the shared auth observer before navigating away
    // from `/login`. This avoids preview-only races where popup/custom-token sign-in resolves
    // before route guards have consumed the matching Firebase auth event.
    logger.notice(
      "Authenticated user reached login route; restoring app destination",
      {
        operation: "login_authenticated_redirect",
        currentLocation: location.pathname,
        queryReturnUrlPresent: Boolean(queryReturnUrl),
        redirectTargetPresent: Boolean(redirectTarget),
      },
    );
    void navigate(redirectTarget, { replace: true });
  }, [
    authenticatedUser,
    loading,
    location.pathname,
    location.search,
    navigate,
    queryReturnUrl,
    redirectTarget,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    if (!token || tokenLoginHandledRef.current) {
      return;
    }

    tokenLoginHandledRef.current = true;

    // Important: do not log raw tokens (they are credentials). Keep logs low-noise.
    logger.info("Detected token-based login attempt", {
      operation: "login_custom_token_detected",
      pathname: location.pathname,
      queryReturnUrlPresent: Boolean(queryReturnUrl),
      redirectTargetPresent: Boolean(redirectTarget),
    });

    const sanitizedLoginParams = new URLSearchParams();

    if (queryReturnUrl) {
      sanitizedLoginParams.set("return_url", queryReturnUrl);
    }

    // Security: remove the custom-token query param from the address bar as soon as we've
    // captured it. Tokens are credentials; leaving them in the URL risks accidental leakage.
    void navigate(
      `${location.pathname}${sanitizedLoginParams.toString() ? `?${sanitizedLoginParams.toString()}` : ""}`,
      {
        replace: true,
        state: location.state,
      },
    );
    logger.notice("Removed one-time login token from the address bar", {
      operation: "login_custom_token_sanitized_url",
      currentLocation: location.pathname,
      sanitizedSearchPresent: sanitizedLoginParams.size > 0,
      queryReturnUrlPresent: Boolean(queryReturnUrl),
    });

    setLoading(true);
    setError("");

    void (async () => {
      try {
        // Keep sessions deterministic: drop any existing Firebase session before signing in.
        await auth.signOut().catch(() => undefined);

        await signInWithCustomToken(auth, token);
        logger.info(
          "Custom token sign-in accepted; waiting for shared auth observer before redirect",
          {
            operation: "login_custom_token_waiting_for_auth_observer",
            redirectTargetPresent: Boolean(redirectTarget),
            loginMode: "token",
            authObserverWaiting: true,
          },
        );
      } catch (loginError) {
        tokenLoginHandledRef.current = false;
        logger.error(
          "Custom token login failed; falling back to manual login",
          loginError instanceof Error ? loginError : undefined,
          { operation: "login_custom_token_failed" },
        );
        setError(adminUiCopy.authLogin.tokenFailure);
      } finally {
        setLoading(false);
      }
    })();
  }, [
    location.pathname,
    location.search,
    location.state,
    navigate,
    queryReturnUrl,
    redirectTarget,
    safeFrom,
  ]);

  const handleEmailLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      logger.debug("Starting email/password login", {
        operation: "login_email_start",
        loginMode: "email",
        pathname: location.pathname,
        queryReturnUrlPresent: Boolean(queryReturnUrl),
        redirectTargetPresent: Boolean(redirectTarget),
        hasRouterStateFrom: Boolean(location.state?.from),
      });

      const result = await signInWithEmailAndPassword(auth, email, password);
      await finalizeLogin({
        redirectTarget,
        loginMode: "email",
        providerId: "password",
        user: result.user,
        successOperation: "login_email_success",
        successMessage: "Email/password admin login successful",
        missingAdminOperation: "login_email_missing_admin",
        missingAdminMessage:
          "User signed in without admin claim; navigation may fail behind protected routes",
      });
    } catch (loginError) {
      logger.error(
        "Email/password login failed",
        loginError instanceof Error ? loginError : undefined,
        { operation: "login_email_failed" },
      );
      setError(
        getPublicErrorMessage(loginError, adminUiCopy.authLogin.genericFailure),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    await handlePopupLogin({
      loginMode: "google",
      provider: new GoogleAuthProvider(),
      startOperation: "login_google_start",
      successOperation: "login_google_success",
      successMessage: "Google admin login successful",
      missingAdminOperation: "login_google_missing_admin",
      missingAdminMessage:
        "User signed in without admin claim; navigation may fail behind protected routes",
      failureOperation: "login_google_failed",
      failureMessage: "Google sign-in failed",
      failureFallbackMessage: "Google sign-in failed",
    });
  };

  const handleXeroLogin = async () => {
    const provider = new OAuthProvider("oidc.xero");
    provider.addScope("openid");
    provider.addScope("email");
    provider.addScope("profile");

    await handlePopupLogin({
      loginMode: "xero",
      provider,
      startOperation: "login_xero_start",
      successOperation: "login_xero_success",
      successMessage: "Xero admin login successful",
      missingAdminOperation: "login_xero_missing_admin",
      missingAdminMessage:
        "User signed in without admin claim; navigation may fail behind protected routes",
      failureOperation: "login_xero_failed",
      failureMessage: "Xero sign-in failed",
      failureFallbackMessage: "Xero sign-in failed",
    });
  };

  const handleQuickBooksLogin = async () => {
    // QuickBooks uses a Firebase OIDC provider configured as `oidc.intuit`.
    const provider = new OAuthProvider("oidc.intuit");

    // Intuit OIDC expects standard scopes.
    provider.addScope("openid");
    provider.addScope("email");
    provider.addScope("profile");

    await handlePopupLogin({
      loginMode: "quickbooks",
      provider,
      startOperation: "login_quickbooks_start",
      successOperation: "login_quickbooks_success",
      successMessage: "QuickBooks admin login successful",
      missingAdminOperation: "login_quickbooks_missing_admin",
      missingAdminMessage:
        "User signed in without admin claim; navigation may fail behind protected routes",
      failureOperation: "login_quickbooks_failed",
      failureMessage: "QuickBooks sign-in failed",
      failureFallbackMessage: "QuickBooks sign-in failed",
    });
  };

  return (
    <div className="admin-login">
      <div aria-hidden="true" className="admin-login__glow" />

      <div className="admin-login__content">
        <div className="admin-login__header">
          <VNextPill tone="accent">Admin</VNextPill>
          <span className="admin-login__eyebrow">
            {adminUiCopy.authLogin.headerEyebrow}
          </span>
        </div>

        <VNextCard
          className="admin-login__card"
          eyebrow={adminUiCopy.authLogin.cardEyebrow}
          subtitle={adminUiCopy.authLogin.subtitle}
          title={adminUiCopy.authLogin.title}
        >
          {signedOutInAnotherTab ? (
            <VNextNotice role="status" tone="info">
              {adminUiCopy.authLogin.signedOutInAnotherTab}
            </VNextNotice>
          ) : null}
          {error ? (
            <VNextNotice role="alert" tone="danger">
              {error}
            </VNextNotice>
          ) : null}

          <form className="admin-login__form" onSubmit={handleEmailLogin}>
            <VNextField label={adminUiCopy.authLogin.emailLabel}>
              <VNextInput
                autoComplete="email"
                autoFocus
                disabled={loading}
                id="email"
                name="email"
                required
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </VNextField>

            <VNextField label={adminUiCopy.authLogin.passwordLabel}>
              <VNextInput
                autoComplete="current-password"
                disabled={loading}
                id="password"
                name="password"
                required
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </VNextField>

            <VNextButton block disabled={loading} tone="primary" type="submit">
              {loading
                ? adminUiCopy.authLogin.emailButtonBusy
                : adminUiCopy.authLogin.emailButton}
            </VNextButton>
          </form>

          <div className="admin-login__divider">
            <span>{adminUiCopy.authLogin.providerDivider}</span>
          </div>

          <div className="admin-login__providers">
            <VNextButton
              block
              disabled={loading}
              tone="secondary"
              onClick={handleGoogleLogin}
            >
              {adminUiCopy.authLogin.googleButton}
            </VNextButton>

            <VNextButton
              block
              disabled={loading}
              tone="secondary"
              onClick={handleXeroLogin}
            >
              {adminUiCopy.authLogin.xeroButton}
            </VNextButton>

            <VNextButton
              block
              disabled={loading}
              tone="secondary"
              onClick={handleQuickBooksLogin}
            >
              {adminUiCopy.authLogin.quickBooksButton}
            </VNextButton>
          </div>

          {loading ? (
            <div
              aria-live="polite"
              className="admin-login__status"
              role="status"
            >
              <span
                aria-hidden="true"
                className="vnext-blocking-card__spinner"
              />
              {adminUiCopy.loading.signInStatus}
            </div>
          ) : null}
        </VNextCard>

        <div className="admin-login__meta">
          {adminUiCopy.authLogin.buildLabelPrefix}: {buildLabel}
        </div>
      </div>
    </div>
  );
};

export default SimpleLogin;
